/**
 * Core types for the attachment background-upload state machine.
 *
 * Web port of `kaether_for_ios/Stoat/E2EE/AttachmentBackgroundUpload.swift`.
 *
 * ## Design notes
 *
 * Progress is tracked via **server-acknowledged bytes only**. The state machine
 * never accepts a "clientBufferedBytes" argument; the type system prevents it.
 * This avoids optimistic progress that can lie: a chunk may be in-flight in
 * the browser's network stack but not yet confirmed by the server — counting
 * those bytes would show progress that turns out to be wrong if the connection
 * drops before the server acks.
 *
 * ## State alphabet
 *
 * ```
 * queued          → upload is queued; no network I/O in progress
 * uploading       → active upload; server-acked-bytes advances
 * waitingNetwork  → no network connectivity; paused, awaiting restoration
 * retryBackoff    → transient server error; waiting for backoff delay to expire
 * completed       → terminal: server confirmed complete receipt
 * failed          → terminal: retry cap exhausted or permanent error
 * ```
 *
 * Both `completed` and `failed` are terminal: no event or backoff tick can
 * leave them.
 *
 * ## `bindToMessageId` invariant
 *
 * An attachment is bound to exactly one messageId. The store enforces this:
 * a second `bindToMessageId(attachmentId, differentMessageId)` call throws.
 * This field intentionally does not appear in any event type — future event
 * additions would therefore not accidentally miss updating a message binding.
 */

// ---------------------------------------------------------------------------
// State alphabet
// ---------------------------------------------------------------------------

/** Upload is queued; network I/O has not yet started. */
export type QueuedState = {
  readonly kind: "queued";
  /** Opaque attachment identifier (server-assigned or local UUID). */
  readonly attachmentId: string;
  /** Total encrypted bytes to upload. */
  readonly totalBytes: number;
};

/** Upload is actively in progress. */
export type UploadingState = {
  readonly kind: "uploading";
  readonly attachmentId: string;
  readonly totalBytes: number;
  /**
   * Bytes acknowledged by the **server** in the last range-ack response.
   * NEVER set from a client-buffer count — see module doc.
   */
  readonly serverAckedBytes: number;
  /** Number of retries consumed so far (carries over from retryBackoff). */
  readonly retryCount: number;
};

/** Network connectivity lost; upload is paused until the network is back. */
export type WaitingNetworkState = {
  readonly kind: "waitingNetwork";
  readonly attachmentId: string;
  readonly totalBytes: number;
  /** Server-acked offset to resume from once connectivity is restored. */
  readonly serverAckedBytes: number;
  readonly retryCount: number;
};

/** Transient server error; waiting for the backoff interval to expire. */
export type RetryBackoffState = {
  readonly kind: "retryBackoff";
  readonly attachmentId: string;
  readonly totalBytes: number;
  /** Server-acked bytes at the point the error occurred. */
  readonly serverAckedBytes: number;
  /** Retries consumed so far (including this one). */
  readonly retryCount: number;
  /** Error code from the server (HTTP status or transport error string). */
  readonly errorCode: string;
  /** Milliseconds since epoch when the backoff expires and upload may resume. */
  readonly backoffUntilMs: number;
};

/** Terminal: server confirmed complete receipt of all bytes. */
export type CompletedState = {
  readonly kind: "completed";
  readonly attachmentId: string;
  readonly totalBytes: number;
};

/** Terminal: retry cap exhausted or permanent (non-retryable) server error. */
export type FailedState = {
  readonly kind: "failed";
  readonly attachmentId: string;
  readonly totalBytes: number;
  readonly errorCode: string;
  readonly retryCount: number;
};

/** Discriminated union of all upload states. */
export type AttachmentBackgroundUploadState =
  | QueuedState
  | UploadingState
  | WaitingNetworkState
  | RetryBackoffState
  | CompletedState
  | FailedState;

// ---------------------------------------------------------------------------
// Events
// ---------------------------------------------------------------------------

/** Consumer signals that upload should begin (or resume). */
export type StartUploadEvent = {
  readonly kind: "startUpload";
};

/**
 * Server acknowledged receipt of `ackedBytes` bytes (from the upload
 * range-ack response). MUST be derived from the server's response, never from
 * client-side buffer tracking.
 */
export type ServerAckedProgressEvent = {
  readonly kind: "serverAckedProgress";
  /** Byte offset confirmed by the server. */
  readonly ackedBytes: number;
};

/** Server returned a transient error (e.g. 503, 429, transient 5xx). */
export type ServerErrorEvent = {
  readonly kind: "serverError";
  /** HTTP status code or transport error string. */
  readonly errorCode: string;
};

/** Server returned a permanent error (e.g. 400, 403, 404, 410). */
export type PermanentErrorEvent = {
  readonly kind: "permanentError";
  readonly errorCode: string;
};

/** Server confirmed that all bytes were received and stored. */
export type UploadCompleteEvent = {
  readonly kind: "uploadComplete";
};

/** Network connectivity was lost. */
export type NetworkLostEvent = {
  readonly kind: "networkLost";
};

/** Network connectivity was restored. */
export type NetworkRestoredEvent = {
  readonly kind: "networkRestored";
};

/** Discriminated union of all events. */
export type AttachmentUploadEvent =
  | StartUploadEvent
  | ServerAckedProgressEvent
  | ServerErrorEvent
  | PermanentErrorEvent
  | UploadCompleteEvent
  | NetworkLostEvent
  | NetworkRestoredEvent;

// ---------------------------------------------------------------------------
// Constructor helpers
// ---------------------------------------------------------------------------

/** Create an initial `queued` state for a new attachment upload. */
export function makeQueuedState(
  attachmentId: string,
  totalBytes: number,
): QueuedState {
  return { kind: "queued", attachmentId, totalBytes };
}
